import { useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Flex, IconCheckmark, LoadingMask, Table } from '@zillow/constellation';
import { Button, Image, Text } from '@zillow/constellation-10';

import { PaymentGatewayContext } from '../PaymentGateway/context';
import { ModularListingContext } from '../../containers/CreateListingPage/context';
import { RMWContext } from '../../providers/RMWContext';

import PropertiesActions from '../../actions/PropertiesActions';
import { default as pgActions } from '../PaymentGateway/actions';
import actions from '../../containers/CreateListingPage/actions';

import PropertyConstants from '../../constants/PropertyConstants';
import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';
import PayAndPublishCSEvents from '../../model/ClickstreamEvent/PayAndPublishCSEvents';
import { RMW_SUB_PATH } from '../../pathsManifest';

import PremiumBadge from '../../images/premium-badge.png';

import LearnAboutPremiumButton, { RENDER_AS_TYPE } from '../LearnAboutPremiumButton';

import { StyledDisplayContainer } from '../StyledComponents/StyledLayout';

import { StyledProductOptionContentContainer, StyledTableCell, StyledTableHeaderCell } from './StyledComponents';

const propTypes = {};
const defaultProps = {};

const defaultCheckMarkColor = 'dark';
const premiumCheckMarkColor = 'blue400';
const premiumBgColor = 'blue100';
const checkMarkSize = 'sm';

const ProductFeatureCompareTable = () => {
  const dispatch = useDispatch();

  const {
    reducerDispatch: pgReducerDispatch,
    pgState: { displayPaymentGateway },
  } = useContext(PaymentGatewayContext);
  const { reducerDispatch: mlReducerDispatch } = useContext(ModularListingContext);
  const { paths } = useContext(RMWContext);

  const { currentProperty, products, listingAlias, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      products: state.properties.currentProperty.freemium.products,
      listingAlias: state.properties.currentProperty.listingAlias,
      user: state.user,
    }),
    shallowEqual,
  );

  const freePrice = products[0]?.price || 0;
  const premiumPrice = products[1]?.price || 0;

  const updateDisplayPaymentGateway = (value) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: DISPLAY_CONTEXT.MODULAR_LISTING,
      },
    });
  };

  const updateSelectedProductType = (selectedProductType) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handlePublishClick = (isPremium) => {
    if (isPremium) {
      PayAndPublishCSEvents.StartActivatePremiumBottom({ currentProperty, user });
    } else {
      PayAndPublishCSEvents.StartActivateFreemiumBottom({ currentProperty, user });
    }

    updateSelectedProductType(
      isPremium ? PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM : PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE,
    );

    const shouldDisplayPaymentGateway = isPremium;
    updateDisplayPaymentGateway(shouldDisplayPaymentGateway);

    if (!shouldDisplayPaymentGateway) {
      actions.updateWizardComplete({ reducerDispatch: mlReducerDispatch, isWizardComplete: true });
      window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`);
    }
  };

  const renderPremiumOption = () => {
    return (
      <StyledProductOptionContentContainer>
        <Text textStyle="body-bold" asChild css={{ marginBottom: '400' }}>
          <p>Get the most out of your listing</p>
        </Text>
        <Text asChild>
          <p>
            Get more exposure on your listing and gain access to deeper insights and analytics to help you price your
            rental and optimize your listing.
          </p>
        </Text>

        <div>
          <Flex display="flex" alignItems="center" flexDirection="row" justifyContent="space-between">
            <div>
              <Text textStyle="heading-xs" asChild>
                <p>{premiumPrice}</p>
              </Text>
              <Text textStyle="body-sm" color="text.subtle" asChild>
                <p>One-time payment</p>
              </Text>
            </div>

            <Image src={PremiumBadge} alt="premium badge" css={{ width: '88px', marginRight: '-2px' }} />
          </Flex>
        </div>

        <Button
          fluid={true}
          tone="brand"
          emphasis="filled"
          css={{ position: 'absolute', bottom: 0 }}
          onClick={() => handlePublishClick(true)}
          data-eventid={PayAndPublishCSEvents.StartActivatePremiumBottom.data_event_id}
        >
          Upgrade to premium
        </Button>
      </StyledProductOptionContentContainer>
    );
  };

  const renderFreeOption = () => {
    return (
      <StyledProductOptionContentContainer>
        <Text textStyle="body-bold" asChild css={{ marginBottom: '400' }}>
          <p>Use our basic listing services</p>
        </Text>
        <Text asChild css={{ marginBottom: '400' }}>
          <p>
            List your property where millions of renters search each month and access our suite of landlord tools to
            screen tenants, sign a lease, and collect rent online.
          </p>
        </Text>

        <Text textStyle="heading-xs" asChild>
          <p>{freePrice}</p>
        </Text>

        <Button
          fluid={true}
          tone="brand"
          emphasis="outlined"
          disabled={displayPaymentGateway}
          css={{ position: 'absolute', bottom: 0 }}
          onClick={() => handlePublishClick(false)}
          data-eventid={PayAndPublishCSEvents.StartActivateFreemiumBottom.data_event_id}
        >
          <LoadingMask loading={displayPaymentGateway}>Continue with free listing</LoadingMask>
        </Button>
      </StyledProductOptionContentContainer>
    );
  };

  return (
    <Table
      appearance="horizontal"
      size="md"
      alignments={['left', 'center', 'center']}
      data-testid="ProductFeatureCompareTable"
    >
      <Table.Header>
        <Table.Row>
          <StyledTableHeaderCell width={{ default: 'inherit', lg: '348px', xxl_gte: '370px' }} />
          <StyledTableHeaderCell
            backgroundColor={premiumBgColor}
            width={{ default: 'inherit', lg: '348px', xxl_gte: '370px' }}
          >
            <Text css={{ textStyle: { default: 'body-xs-bold', xl: 'heading-xs' } }}>Premium</Text>
          </StyledTableHeaderCell>
          <StyledTableHeaderCell width={{ default: 'inherit', lg: '348px', xxl_gte: '370px' }}>
            <Text css={{ textStyle: { default: 'body-xs-bold', xl: 'heading-xs' } }}>Basic</Text>
          </StyledTableHeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>
              Syndication to Trulia, HotPads, and Zillow
            </Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Applications</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Online lease signing</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Lease builder (in select markets)</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Online payments</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>App / messaging platform</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell>
            <IconCheckmark size={checkMarkSize} fontColor={defaultCheckMarkColor} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>More exposure</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Listing recommendations</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Pricing &amp; market insights</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <StyledTableCell removeLeftPadding={true}>
            <Text css={{ textStyle: { default: 'body-sm', xl: 'body-bold' } }}>Priority support</Text>
          </StyledTableCell>
          <StyledTableCell backgroundColor={premiumBgColor}>
            <IconCheckmark size={checkMarkSize} fontColor={premiumCheckMarkColor} />
          </StyledTableCell>
          <Table.Cell />
        </Table.Row>

        <StyledDisplayContainer display={{ default: 'none', xxl_gte: 'contents' }}>
          <Table.Row>
            <StyledTableCell removeBorderBottom={true} removeLeftPadding={true}>
              <LearnAboutPremiumButton as={RENDER_AS_TYPE.TEXT_BUTTON}>
                Learn more about premium
              </LearnAboutPremiumButton>
            </StyledTableCell>
            <StyledTableCell removeBorderBottom={true} backgroundColor={premiumBgColor} height="270px">
              {renderPremiumOption()}
            </StyledTableCell>
            <StyledTableCell removeBorderBottom={true} height="270px">
              {renderFreeOption()}
            </StyledTableCell>
          </Table.Row>
        </StyledDisplayContainer>
      </Table.Body>
    </Table>
  );
};

ProductFeatureCompareTable.propTypes = propTypes;
ProductFeatureCompareTable.defaultProps = defaultProps;

export default ProductFeatureCompareTable;
